import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { uuidv7 } from 'uuidv7';
import type { DiagnosticConsideration, DiagnosticItem } from '@/domain/schema/v1';
import { useAutosave } from '../../hooks/useAutosave';
import { useLoad } from '../../hooks/useLoad';
import { useService } from '../../hooks/useService';
import { ConfirmButton, SaveIndicator, StringListEditor, TextArea } from '../../components/fields';
import type { ClientOutlet } from './ClientShell';

const STATUS: { value: DiagnosticItem['status']; label: string }[] = [
  { value: 'insufficient_data', label: '자료 부족' },
  { value: 'under_review', label: '검토 중' },
  { value: 'possible', label: '가능성 있음' },
  { value: 'professional_evaluation_recommended', label: '전문평가 권고' },
  { value: 'externally_confirmed', label: '공식 진단 (외부 확인)' },
];
const label = (s: string) => STATUS.find((x) => x.value === s)?.label ?? s;
const SOURCE: Record<DiagnosticItem['source'], string> = {
  'ai-proposal': 'AI 제안',
  counselor: '상담자',
  'external-confirmation': '외부 확인',
};

/**
 * 진단적 고려 (MASTER §10): 확정이 아니라 구조화. '공식 진단 확인됨'은 외부 확인 정보만.
 * 화면은 사례개념화와 같은 틀: 묶음(증상 · 고려할 진단 · 평가와 의뢰) 안에 칸마다 박스, 진단마다 카드.
 */
export function DiagnosisScreen() {
  const { client } = useOutletContext<ClientOutlet>();
  const svc = useService();
  const { data, reload } = useLoad(async () => {
    const [list, discarded] = await Promise.all([
      svc.listDiagnosis(client.id),
      svc.discardedDiagnosisProposals(client.id),
    ]);
    return { list, discarded };
  }, [svc, client.id]);
  const [justDiscarded, setJustDiscarded] = useState<string | null>(null);
  if (!data) return <p className="muted">불러오는 중…</p>;
  const open = data.list.find((d) => d.status === 'proposal');
  const current = data.list.filter((d) => d.status === 'approved').at(-1);
  const restore = (id: string) =>
    void svc.restoreDiagnosisProposal(id).then(() => {
      setJustDiscarded(null);
      reload();
    });
  return (
    <div className="stack">
      <p className="muted small">
        공식 진단이 아니라 확인할 것을 정리하는 곳이에요. AI는 진단을 확정할 수 없고, "공식 진단"은 외부에서 확인된
        진단을 직접 넣을 때만 써요. 외부 AI가 만든 개정안은 회기 진행의 4 검토에서도 승인할 수 있어요.
      </p>
      {justDiscarded && !open && (
        <p className="small undo-line" role="status">
          개정안을 버렸어요.{' '}
          <button type="button" className="link" onClick={() => restore(justDiscarded)}>
            되돌리기
          </button>
        </p>
      )}
      {open ? (
        <>
          {current && (
            <details className="card">
              <summary>
                <strong>현재 승인된 진단적 고려 v{current.version}</strong>{' '}
                <span className="muted small">펼쳐서 개정안과 비교</span>
              </summary>
              <DxView d={current} />
            </details>
          )}
          <Editor
            key={`${open.id}:${open.revision}`}
            d={open}
            onApproved={reload}
            onDiscarded={(id) => {
              setJustDiscarded(id);
              reload();
            }}
          />
        </>
      ) : (
        <>
          {current ? (
            <div className="card stack">
              <h2>현재 진단적 고려 v{current.version} (승인)</h2>
              <DxView d={current} />
            </div>
          ) : (
            <div className="card">
              <strong>현재 진단적 고려</strong>
              <p className="muted small">아직 승인된 것이 없어요.</p>
            </div>
          )}
          <div className="card">
            <strong>개정안</strong>
            <p className="muted small">지금 검토할 개정안이 없어요.</p>
            <button
              type="button"
              className="primary"
              onClick={() => void svc.createDiagnosisProposal(client.id).then(reload)}
            >
              {current ? '진단적 고려 개정안 만들기 (현재 버전에서 시작)' : '진단적 고려 작성 시작'}
            </button>
          </div>
          {data.discarded.length > 0 && (
            <div className="card">
              <strong>버린 개정안</strong>
              <ul className="list">
                {data.discarded.map((d) => (
                  <li key={d.id} className="list-row">
                    <span>
                      v{d.version} · 버린 날 {d.deletedAt?.slice(0, 10)} · 고려할 진단 {d.considerations.length}개
                    </span>
                    <button type="button" onClick={() => restore(d.id)}>
                      되돌리기
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
      {data.list.length > 1 && <p className="muted small">이전 버전 {data.list.length - 1}개가 보존되어 있습니다.</p>}
    </div>
  );
}

/** 칸 머리: 이름 · 개수 · 무엇을 쓰는 칸인지 */
function Head({ name, count, help }: { name: string; count?: number; help?: string }) {
  return (
    <div className="fm-field-head">
      <strong>{name}</strong>
      {count !== undefined && (
        <span className={count ? 'status-pill' : 'status-pill empty'}>{count ? `${count}개` : '비어 있음'}</span>
      )}
      {help && <span className="muted small">{help}</span>}
    </div>
  );
}

function Group({ title, desc, children }: { title: string; desc?: string; children: React.ReactNode }) {
  return (
    <section className="fm-group">
      <div className="fm-group-head">
        <h3>{title}</h3>
        {desc && <p className="muted small">{desc}</p>}
      </div>
      {children}
    </section>
  );
}

function Bullets({ items }: { items?: string[] | undefined }) {
  const list = (items ?? []).filter((x) => x.trim());
  return list.length ? (
    <ul className="fm-list">
      {list.map((x, i) => (
        <li key={i}>{x}</li>
      ))}
    </ul>
  ) : null;
}

/** 승인된 버전 보기 (편집 화면과 같은 틀) */
function DxView({ d }: { d: DiagnosticConsideration }) {
  return (
    <div className="stack fm-view">
      <Group title="증상">
        <div className="fm-field">
          <Head name="현재 증상" count={d.symptomList?.length ?? 0} />
          <Bullets items={d.symptomList} />
        </div>
      </Group>
      <Group title={`고려할 진단 (${d.considerations.length})`}>
        {d.considerations.length === 0 && <p className="muted small">없음</p>}
        {d.considerations.map((c, i) => (
          <div key={c.id} className="fm-field dx-card">
            <div className="dx-head">
              <span className="opinion-no">{i + 1}</span>
              <strong className="dx-name">{c.diagnosisName}</strong>
              <span className={`status-pill dx-${c.status}`}>{label(c.status)}</span>
              <span className="muted small">{SOURCE[c.source]}</span>
            </div>
            {(c.criteriaToAssess?.length ?? 0) > 0 && (
              <div className="dx-sub">
                <span className="dx-sub-title">확인할 기준</span>
                <Bullets items={c.criteriaToAssess} />
              </div>
            )}
            {(c.differentialConsiderations?.length ?? 0) > 0 && (
              <div className="dx-sub">
                <span className="dx-sub-title">감별할 가능성</span>
                <Bullets items={c.differentialConsiderations} />
              </div>
            )}
          </div>
        ))}
      </Group>
      <Group title="평가와 의뢰">
        <div className="fm-field">
          <Head name="추가 평가·검사" count={d.recommendedAssessment?.length ?? 0} />
          <Bullets items={d.recommendedAssessment} />
        </div>
        <div className="fm-field">
          <Head name="외부 의뢰" />
          <p className="small" style={{ margin: 0 }}>
            {d.referralRecommended ? `필요${d.referralReason ? ` — ${d.referralReason}` : ''}` : '필요 없음'}
          </p>
        </div>
      </Group>
    </div>
  );
}

function Editor({
  d,
  onApproved,
  onDiscarded,
}: {
  d: DiagnosticConsideration;
  onApproved: () => void;
  onDiscarded: (id: string) => void;
}) {
  const svc = useService();
  const a = useAutosave<DiagnosticConsideration>(d, (patch, rev) => svc.updateDiagnosis(d.id, patch, rev));
  const [removed, setRemoved] = useState<{ item: DiagnosticItem; index: number } | null>(null);
  const v = a.value!;
  const items = v.considerations;
  const up = (i: number, patch: Partial<DiagnosticItem>) =>
    a.set(
      'considerations',
      items.map((x, j) => (j === i ? { ...x, ...patch } : x)),
    );
  return (
    <div className="card stack">
      <div className="card-head">
        <h2>개정안 v{d.version} (제안)</h2>
        <SaveIndicator status={a.status} error={a.error} />
      </div>
      {items.length > 0 && (
        <nav className="fm-index" aria-label="진단 바로가기">
          {items.map((c, i) => (
            <a key={c.id} href={`#dx-${c.id}`} className="fm-chip">
              {i + 1}. {c.diagnosisName || '(이름 없음)'} · {label(c.status)}
            </a>
          ))}
        </nav>
      )}

      <Group title="증상" desc="관찰되었거나 내담자가 보고한 증상">
        <div className="fm-field">
          <Head name="현재 증상" count={v.symptomList?.length ?? 0} help="한 항목에 증상 하나" />
          <StringListEditor label="현재 증상" value={v.symptomList} onChange={(x) => a.set('symptomList', x)} />
        </div>
      </Group>

      <Group
        title={`고려할 진단 (${items.length})`}
        desc="진단마다 상태를 고르고, 더 확인할 기준과 감별할 가능성을 적어요"
      >
        {removed && (
          <p className="small undo-line" role="status">
            지움: {removed.item.diagnosisName}{' '}
            <button
              type="button"
              className="link"
              onClick={() => {
                const next = [...items];
                next.splice(Math.min(removed.index, next.length), 0, removed.item);
                a.set('considerations', next);
                setRemoved(null);
              }}
            >
              되돌리기
            </button>
          </p>
        )}
        {items.map((c, i) => (
          <div key={c.id} id={`dx-${c.id}`} className="fm-field dx-card">
            <div className="dx-head">
              <span className="opinion-no">{i + 1}</span>
              <input
                className="dx-name-input"
                aria-label={`진단 ${i + 1}`}
                value={c.diagnosisName}
                onChange={(e) => up(i, { diagnosisName: e.target.value })}
              />
              <span className="muted small">{SOURCE[c.source]}</span>
            </div>
            <div className="dx-status" role="group" aria-label={`진단 ${i + 1} 상태`}>
              {STATUS.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  className={c.status === s.value ? `pill-btn on dx-${s.value}` : 'pill-btn'}
                  aria-pressed={c.status === s.value}
                  onClick={() =>
                    up(i, {
                      status: s.value,
                      source: s.value === 'externally_confirmed' ? 'external-confirmation' : c.source,
                    })
                  }
                >
                  {s.label}
                </button>
              ))}
            </div>
            {c.status === 'externally_confirmed' && (
              <p className="warn small" style={{ margin: 0 }}>
                정신과 등 외부 전문가가 확인한 공식 진단일 때만 선택하세요.
              </p>
            )}
            <div className="dx-sub">
              <span className="dx-sub-title">확인할 기준</span>
              <StringListEditor
                label={`진단 ${i + 1} 확인할 기준`}
                value={c.criteriaToAssess}
                onChange={(x) => up(i, { criteriaToAssess: x })}
              />
            </div>
            <div className="dx-sub">
              <span className="dx-sub-title">감별할 가능성</span>
              <StringListEditor
                label={`진단 ${i + 1} 감별할 가능성`}
                value={c.differentialConsiderations}
                onChange={(x) => up(i, { differentialConsiderations: x })}
              />
            </div>
            <button
              type="button"
              className="link small dx-del"
              onClick={() => {
                setRemoved({ item: c, index: i });
                a.set(
                  'considerations',
                  items.filter((_, j) => j !== i),
                );
              }}
            >
              이 진단 빼기
            </button>
          </div>
        ))}
        <button
          type="button"
          className="dx-add"
          onClick={() =>
            a.set('considerations', [
              ...items,
              { id: uuidv7(), diagnosisName: '고려할 진단', status: 'under_review', source: 'counselor' },
            ])
          }
        >
          + 고려할 진단 추가
        </button>
      </Group>

      <Group title="평가와 의뢰">
        <div className="fm-field">
          <Head
            name="추가 평가·검사"
            count={v.recommendedAssessment?.length ?? 0}
            help="더 해 볼 검사나 평가 (예: 우울 척도, 정신과 평가)"
          />
          <StringListEditor
            label="추가 평가·검사"
            value={v.recommendedAssessment}
            onChange={(x) => a.set('recommendedAssessment', x)}
          />
        </div>
        <div className="fm-field">
          <Head name="외부 의뢰" />
          <label className="check">
            <input
              type="checkbox"
              checked={!!v.referralRecommended}
              onChange={(e) => a.set('referralRecommended', e.target.checked)}
            />{' '}
            외부 의뢰 필요
          </label>
          {v.referralRecommended && (
            <TextArea
              label="의뢰 사유"
              value={v.referralReason}
              onChange={(x) => a.set('referralReason', x || undefined)}
            />
          )}
        </div>
      </Group>

      <TextArea label="변경 이유" value={v.changeReason} onChange={(x) => a.set('changeReason', x || undefined)} />
      <ConfirmButton
        className="primary"
        label="이 개정안 승인"
        confirmText="상담자 판단으로 승인합니다. 이전 버전은 보존됩니다."
        onConfirm={() =>
          void a
            .flush()
            .then(() => svc.approveDiagnosis(d.id))
            .then(onApproved)
        }
      />
      <ConfirmButton
        className="link small"
        label="이 개정안 버리기"
        confirmText="이 개정안을 버릴까요? 현재(승인된) 진단적 고려는 그대로이고, 버린 뒤에도 되돌릴 수 있어요."
        onConfirm={() =>
          void a
            .flush()
            .then(() => svc.discardDiagnosisProposal(d.id))
            .then(() => onDiscarded(d.id))
        }
      />
    </div>
  );
}
